// src/private/src/pages/app/leads/LeadDetailPage.jsx

import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Card, CardHeader, CardContent, Typography,
  CircularProgress, Divider, Button, IconButton, TextField, Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '@src/api/clients';

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath =
    typeof location.state?.from === 'string' && location.state.from.startsWith('/')
      ? location.state.from
      : '/leads';

  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [editMode, setEditMode] = useState({});
  const [loading, setLoading] = useState(true);

  const NON_EDITABLE_FIELDS = [
    'processed',
    'post_response',
    'success_post',
    'date_created',
    'webhook_sent_datetime',
    'response_message',
    'failed_webhook_attempts',
  ];
  

  useEffect(() => {
    api.request({ url: `dashboard/leads/${leadId}/`, method: 'GET' })
      .then((res) => {
        setFormData(res.data);
        setOriginalData(res.data);
      })
      .catch((err) => console.error('Error fetching lead:', err))
      .finally(() => setLoading(false));
  }, [leadId]);

  const handleEditToggle = (section) => {
    setEditMode((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const SectionCard = ({ sectionKey, title, fields }) => {
    const isEditing = editMode[sectionKey];
    const [sectionForm, setSectionForm] = useState({});

    useEffect(() => {
      if (isEditing) {
        const initial = {};
        fields.forEach(({ name }) => {
          initial[name] = formData[name] || '';
        });
        setSectionForm(initial);
      }
    }, [isEditing, formData, fields]);

    const handleFieldChange = (name, value) => {
      setSectionForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleSaveSection = async () => {
        const cleanedData = {};
      
        fields.forEach(({ name }) => {
          const rawVal = sectionForm[name];
          if (rawVal === '') {
            cleanedData[name] = null;
          } else if (typeof rawVal === 'string') {
            const lower = rawVal.toLowerCase();
            if (lower === 'true') {
              cleanedData[name] = true;
            } else if (lower === 'false') {
              cleanedData[name] = false;
            } else {
              cleanedData[name] = rawVal;
            }
          } else {
            cleanedData[name] = rawVal;
          }
        });
      
        console.log('🛰️ Submitting cleaned payload:', cleanedData);
      
        try {
          await api.request({
            url: `dashboard/leads/${leadId}/`,
            method: 'PUT',
            payload: { ...formData, ...cleanedData },  // Merge into full payload
          });
      
          setFormData((prev) => ({ ...prev, ...cleanedData }));
          setOriginalData((prev) => ({ ...prev, ...cleanedData }));
          setEditMode((prev) => ({ ...prev, [sectionKey]: false }));
        } catch (err) {
          console.error('❌ Failed to save section:', err);
          alert('Failed to save changes.');
        }
      };
      
      
      
      

    const handleCancelSection = () => {
      setEditMode((prev) => ({ ...prev, [sectionKey]: false }));
      setSectionForm({});
    };

    return (
      <Card sx={{ backgroundColor: isEditing ? '#fffde7' : 'white', border: isEditing ? '1px solid #fbc02d' : undefined }}>
        <CardHeader
          title={title}
          action={
            isEditing ? (
              <Stack direction="row" spacing={1}>
                <IconButton onClick={() => handleSaveSection(sectionKey)}><SaveIcon fontSize="small" /></IconButton>

                <IconButton onClick={handleCancelSection}><CloseIcon fontSize="small" /></IconButton>
              </Stack>
            ) : (
              <IconButton onClick={() => handleEditToggle(sectionKey)}><EditIcon fontSize="small" /></IconButton>
            )
          }
        />
        <Divider />
        <CardContent>
          <Stack spacing={2}>
          {fields.map(({ label, name }) => (
            <Box key={name}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                {isEditing && !NON_EDITABLE_FIELDS.includes(name) ? (
                <TextField
                    value={sectionForm[name] ?? ''}
                    onChange={(e) => handleFieldChange(name, e.target.value)}
                    fullWidth
                    size="small"
                />
                ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {['date_created', 'webhook_sent_datetime'].includes(name) && formData[name]
                      ? new Date(formData[name]).toLocaleString()
                      : formData[name] === true
                      ? '✅ True'
                      : formData[name] === false
                      ? '❌ False'
                      : formData[name] || '—'}
                  </Typography>
                  
                )}
            </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Button onClick={() => navigate(backPath)} startIcon={<ArrowBackIcon />} sx={{ mb: 3 }}>
        Back to Leads
      </Button>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SectionCard sectionKey="basic" title="🧍 Basic Info" fields={[
            { label: 'First Name', name: 'first_name' },
            { label: 'Last Name', name: 'last_name' },
            { label: 'Email', name: 'email' },
            { label: 'Phone', name: 'phone' },
            { label: 'Date Created (EST)', name: 'date_created', editable: false }, // 🔒
            
          ]} />
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard sectionKey="posting" title="🚀 Posting Info" fields={[
           { label: 'Webhook Sent (EST)', name: 'webhook_sent_datetime', editable: false }, // 🔒
           { label: 'Processed', name: 'processed', editable: false },   
           { label: 'Success Post', name: 'success_post', editable: false },          // 🔒
           { label: 'Post Response', name: 'post_response', editable: false }, 
            { label: 'Response Message', name: 'response_message' , editable: false },
            { label: 'Failed Webhook Attempts', name: 'failed_webhook_attempts' , editable: false },
          ]} />
        </Grid>
        {Array.isArray(formData.related_backend_data) && formData.related_backend_data.length > 0 && (
  formData.related_backend_data.map((record, index) => (
    <Grid item xs={12} md={6} key={index}>
      <Card sx={{ backgroundColor: 'white' }}>
        <CardHeader title={`📊 Backend Data Record #${index + 1}`} />
        <Divider />
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">Created Date</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{record.created_date || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Appointment Date</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{record.appointment_date || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Issue</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{record.issue ? '✅ True' : '❌ False'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Demo</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{record.demo ? '✅ True' : '❌ False'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Sold Date</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{record.sold_date || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Gross Amount</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{record.gross_amount || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Net Amount</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{record.net_amount || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{record.status || '—'}</Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  ))
)}



          
        <Grid item xs={12} md={4}>
          <SectionCard sectionKey="location" title="📍 Location Info" fields={[
            { label: 'Address', name: 'address' },
            { label: 'City', name: 'city' },
            { label: 'State', name: 'state' },
            { label: 'ZIP Code', name: 'zip_code' },
            { label: 'Territory', name: 'territory' },
          ]} />
        </Grid>

        <Grid item xs={12} md={4}>
          <SectionCard sectionKey="self_book" title="📆 Self-Book Info" fields={[
            { label: 'Appointment DateTime', name: 'self_book_appointment_date_time' },
            { label: 'Spouse', name: 'self_book_spouse' },
            { label: 'Notes', name: 'self_book_notes' },
          ]} />
        </Grid>

        <Grid item xs={12} md={4}>
          <SectionCard sectionKey="source" title="📢 Source Info" fields={[
            { label: 'Source', name: 'source' },
            { label: 'Medium', name: 'medium' },
            { label: 'Campaign', name: 'campaign_name' },
            { label: 'Ad Set', name: 'ad_set_name' },
            { label: 'Ad Name', name: 'ad_name' },
            { label: 'Vertical', name: 'vertical' },
          ]} />
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard sectionKey="tracking" title="🕵️ Tracking Info" fields={[
            { label: 'FBC', name: 'fbc' },
            { label: 'FBP', name: 'fbp' },
            { label: 'IP Address', name: 'ip_address' },
            { label: 'User Agent', name: 'user_agent' },
            { label: 'Trusted Form', name: 'trusted_form' },
          ]} />
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard sectionKey="fb_lead" title="🧠 FB Lead IDs" fields={[
            { label: 'Lead FBTrace ID', name: 'fb_lead_fbtrace_id' },
            { label: 'CompleteReg FBTrace ID', name: 'fb_completeregistration_fbtrace_id' },
          ]} />
        </Grid>
      </Grid>
    </Box>
  );
}
